import { Checkbox } from '@perch-labs/shrike';
import { useEffect, useState } from 'react';
import { Text } from '~/components/typography';
import { PreferenceCheckboxRow } from './customer-notification-preference.style';

export const NOTIFICATION_PREFERENCE_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'transactional_sms', label: 'Text' },
  { value: 'physical_mail', label: 'Mail' },
  { value: 'phone', label: 'Phone' },
] as const;

export type NotificationPreference = (typeof NOTIFICATION_PREFERENCE_OPTIONS)[number]['value'];

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreference[] = [
  'email',
  'transactional_sms',
];

interface CustomerNotificationPreferenceProps {
  title: string;
  description: string;
  requiredMessage: string;
  onChange: (
    value: NotificationPreference[],
    meta: { name: 'notificationPreferences'; isValid: boolean },
  ) => void;
}

const orderedSelection = (selected: readonly NotificationPreference[]): NotificationPreference[] =>
  NOTIFICATION_PREFERENCE_OPTIONS.map(option => option.value).filter(value =>
    selected.includes(value),
  );

const preferenceField = (selected: readonly NotificationPreference[]) => ({
  name: 'notificationPreferences' as const,
  isValid: selected.length > 0,
});

export const CustomerNotificationPreference = ({
  title,
  description,
  requiredMessage,
  onChange,
}: CustomerNotificationPreferenceProps) => {
  const [selected, setSelected] = useState<NotificationPreference[]>(
    DEFAULT_NOTIFICATION_PREFERENCES,
  );

  useEffect(() => {
    onChange(selected, preferenceField(selected));
    // The default selection has to count as a completed field before the customer edits it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (preference: NotificationPreference, checked: boolean) => {
    const next = orderedSelection(
      checked ? [...selected, preference] : selected.filter(value => value !== preference),
    );
    setSelected(next);
    onChange(next, preferenceField(next));
  };

  return (
    <>
      <Text textStyle="paragraph400" fontWeight="700" mt={4}>
        {title}
      </Text>
      <Text textStyle="paragraph400" color="secondary" mb={2}>
        {description}
      </Text>
      <PreferenceCheckboxRow>
        {NOTIFICATION_PREFERENCE_OPTIONS.map(option => (
          <Checkbox
            key={option.value}
            name={option.value}
            margin="8px 0"
            data-testid={`notification-preference-${option.value}`}
            checked={selected.includes(option.value)}
            onChange={event => toggle(option.value, event.target.checked)}
          >
            {option.label}
          </Checkbox>
        ))}
      </PreferenceCheckboxRow>
      {selected.length === 0 ? (
        <Text textStyle="paragraph400" color="error">
          {requiredMessage}
        </Text>
      ) : null}
    </>
  );
};
